import { getAuth } from 'firebase/auth';
import { app } from '@/config/firebaseConfig';
import { batchGetUploadUrlEndpoint } from '@/config/endpoints';
import { feBot } from '@/monitor';
import { ImgUploadRP } from './RP';

type GetUploadFileParams = {
  extension: string;
  content_type: string;
};
type GetUploadFileListParams = {
  upload_list: GetUploadFileParams[];
};

type GetUploadFileListRes = {
  upload_result: {
    upload_url: string;
    file_id: string;
  }[];
};

type UploadImgFileRes = {
  headers: Headers;
  ok: boolean;
  redirected: boolean;
  status: number;
  statusText: string;
  url: string;
};

export const getUploadUrls: (
  imgList: File[]
) => Promise<GetUploadFileListRes['upload_result']> = async (imgList) => {
  try {
    const fileTypeList = imgList.map((file) => {
      return {
        extension: file.name.split('.').pop() || file.type.split('/')[1],
        content_type: file.type,
      };
    });

    const getUploadUrlsReq: GetUploadFileListParams = {
      upload_list: fileTypeList,
    };

    const auth = getAuth(app);
    await auth.authStateReady();
    const authToken = auth.currentUser
      ? await auth.currentUser.getIdToken()
      : null;

    const response = await fetch(batchGetUploadUrlEndpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(getUploadUrlsReq),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText);
    }

    const getUploadUrlsRes: GetUploadFileListRes = await response.json();
    const upload_result = getUploadUrlsRes.upload_result;

    // console.log('getUploadUrls - getUploadUrlsRes:', getUploadUrlsRes);

    return upload_result;
  } catch (error) {
    console.error('getUploadUrls - error:', error);
    throw new Error(`getUploadUrls - error: ${error}`);
  }
};

export const uploadImgFile_v2 = async (
  uploadUrl: string,
  file: File,
  from: string = 'uploadImgFile_v2_default_from'
) => {
  const {
    batch_upload_img_request,
    batch_upload_img_success,
    batch_upload_img_fail,
  } = ImgUploadRP;

  try {
    batch_upload_img_request({
      // bytes换算为MB
      fileSize: file?.size ? Number((file.size / 1024 / 1024).toFixed(2)) : 0,
      fileType: file?.type,
    });

    const response: UploadImgFileRes = await fetch(
      decodeURIComponent(uploadUrl),
      { method: 'PUT', headers: { 'Content-Type': file.type }, body: file }
    );

    if (response.ok) {
      batch_upload_img_success({
        // bytes换算为MB
        fileSize: file?.size ? Number((file.size / 1024 / 1024).toFixed(2)) : 0,
        fileType: file?.type,
      });

      // console.log('上传成功！response:', response);
      return { success: true, url: response.url };
    } else {
      const text = response.statusText;
      const errorMessage = `上传失败，状态码：${response.status}，响应内容：${text}, 所有错误信息: ${JSON.stringify(response)}`;

      feBot.lark_report({
        title: '[UPLOAD_ERROR] uploadImgFile_v2 response not ok',
        type: from,
        errorInfo: errorMessage,
      });

      batch_upload_img_fail({
        title: '[UPLOAD_ERROR] uploadImgFile_v2 response not ok',
        type: from,
        errorInfo: errorMessage,
        fileSize: file?.size ? Number((file.size / 1024 / 1024).toFixed(2)) : 0,
        fileType: file?.type,
      });

      console.error(errorMessage);
    }
  } catch (error) {
    // 构建更详细的错误信息
    let errorMessage = 'Unknown error';
    let errorStack = '';
    let errorName = '';
    let errorCode = '';
    let detailedErrorInfo = '';

    if (error instanceof Error) {
      errorMessage = error.message;
      errorStack = error.stack || '';
      errorName = error.name;
    }

    // 处理网络错误的特殊情况
    if (error instanceof TypeError && error.message.includes('fetch')) {
      errorMessage = `Network error: ${error.message}`;
      detailedErrorInfo = '可能是网络连接问题或CORS问题';
    }

    // 处理其他特殊错误类型
    if (error && typeof error === 'object') {
      try {
        // 尝试提取更多错误信息
        const errorObj = error as any;
        if (errorObj.code) errorCode = errorObj.code;
        if (errorObj.status) errorCode = errorObj.status.toString();
        if (errorObj.statusText) detailedErrorInfo = errorObj.statusText;
      } catch (e) {
        // 忽略解析错误
      }
    }

    // 构建完整的错误报告
    const fullErrorInfo = {
      message: errorMessage,
      name: errorName,
      code: errorCode,
      stack: errorStack,
      detailedInfo: detailedErrorInfo,
      uploadUrl: uploadUrl ? '***REDACTED***' : 'not_provided', // 隐藏敏感URL信息
      fileName: file?.name || 'unknown',
      // bytes换算为MB
      fileSize: file?.size ? (file.size / 1024 / 1024).toFixed(2) + 'MB' : 0,
      fileType: file?.type || 'unknown',
      from,
      timestamp: new Date().toISOString(),
      userAgent:
        typeof window !== 'undefined' ? window.navigator.userAgent : 'server',
      originalError:
        error instanceof Error
          ? { toString: error.toString(), valueOf: error.valueOf() }
          : String(error),
    };

    feBot.lark_report({
      title: '[UPLOAD_ERROR] uploadImgFile_v2 error',
      type: errorMessage,
      errorInfo: JSON.stringify(fullErrorInfo, null, 2),
    });

    batch_upload_img_fail({
      title: '[UPLOAD_ERROR] uploadImgFile_v2 error',
      type: from,
      errorInfo: errorMessage,
      fileSize: file?.size ? Number((file.size / 1024 / 1024).toFixed(2)) : 0,
      fileType: file?.type,
    });

    console.error('uploadImgFile_v2 详细错误信息:', {
      errorMessage,
      errorName,
      errorCode,
      errorStack,
      detailedErrorInfo,
      fileName: file?.name,
      fileSize: file?.size,
      fileType: file?.type,
      from,
    });
  }
};
